import { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { api, type WorkOrder } from '../../lib/api';
import { useOptimisticMutation, type RowPatch } from '../../hooks/useOptimisticMutation';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { LocationSelector } from '../ui/LocationSelector';

// =============================================================================
// STATUS CONFIGURATION
// =============================================================================

export const WORK_ORDER_STATUSES = [
  // Stage 0 - New
  { value: '0. New Workorder', label: 'New Workorder', variant: 'warning' as const, stage: 0 },
  { value: '0. Returned', label: 'Returned', variant: 'earth' as const, stage: 0 },
  // Stage 1 - Processing
  { value: '1. Contact Made', label: 'Contact Made', variant: 'info' as const, stage: 1 },
  { value: '1. To Order', label: 'To Order', variant: 'info' as const, stage: 1 },
  { value: '1. Floral Department', label: 'Floral Department', variant: 'info' as const, stage: 1 },
  { value: '1. In House', label: 'In House', variant: 'info' as const, stage: 1 },
  // Stage 2 - Ordered
  { value: '2. Ordered', label: 'Ordered', variant: 'default' as const, stage: 2 },
  // Stage 3 - Received
  { value: '3. Received', label: 'Received', variant: 'default' as const, stage: 3 },
  // Stage 4 - Ready
  { value: '4. Ready for Pickup', label: 'Ready for Pickup', variant: 'success' as const, stage: 4 },
  { value: '4. Ready for Run', label: 'Ready for Run', variant: 'success' as const, stage: 4 },
  { value: '4. For South Bay', label: 'For South Bay', variant: 'success' as const, stage: 4 },
  // Stage 5 - Complete
  { value: '5. Complete', label: 'Complete', variant: 'success' as const, stage: 5 },
  // Stage 6/7 - Declined
  { value: '6. To Decline', label: 'To Decline', variant: 'danger' as const, stage: 6 },
  { value: '7. Declined', label: 'Declined', variant: 'danger' as const, stage: 7 },
];

// Group statuses by stage for Kanban view
export const STATUS_STAGES = [
  { stage: 0, label: 'New', statuses: ['0. New Workorder', '0. Returned'] },
  { stage: 1, label: 'Processing', statuses: ['1. Contact Made', '1. To Order', '1. Floral Department', '1. In House'] },
  { stage: 2, label: 'Ordered', statuses: ['2. Ordered'] },
  { stage: 3, label: 'Received', statuses: ['3. Received'] },
  { stage: 4, label: 'Ready', statuses: ['4. Ready for Pickup', '4. Ready for Run', '4. For South Bay'] },
  { stage: 5, label: 'Complete', statuses: ['5. Complete'] },
  { stage: 6, label: 'Declined', statuses: ['6. To Decline', '7. Declined'] },
];

// Valid status transitions - defines what statuses each status can move to
const STATUS_TRANSITIONS: Record<string, string[]> = {
  // New -> Processing or Declined
  '0. New Workorder': ['1. Contact Made', '1. To Order', '1. Floral Department', '1. In House', '6. To Decline'],
  '0. Returned': ['1. Contact Made', '1. To Order', '1. Floral Department', '1. In House', '6. To Decline'],
  // Processing -> Ordered, Ready, Complete, or Declined
  '1. Contact Made': ['1. To Order', '1. Floral Department', '1. In House', '2. Ordered', '4. Ready for Pickup', '4. Ready for Run', '4. For South Bay', '5. Complete', '6. To Decline'],
  '1. To Order': ['2. Ordered', '6. To Decline'],
  '1. Floral Department': ['2. Ordered', '4. Ready for Pickup', '4. Ready for Run', '5. Complete', '6. To Decline'],
  '1. In House': ['2. Ordered', '4. Ready for Pickup', '4. Ready for Run', '4. For South Bay', '5. Complete', '6. To Decline'],
  // Ordered -> Received
  '2. Ordered': ['3. Received', '6. To Decline'],
  // Received -> Ready
  '3. Received': ['4. Ready for Pickup', '4. Ready for Run', '4. For South Bay', '6. To Decline'],
  // Ready -> Complete
  '4. Ready for Pickup': ['5. Complete'],
  '4. Ready for Run': ['5. Complete'],
  '4. For South Bay': ['5. Complete'],
  // Complete is terminal
  '5. Complete': [],
  // Declined states
  '6. To Decline': ['7. Declined', '0. New Workorder'], // Can confirm decline or reopen
  '7. Declined': ['0. New Workorder'], // Can reopen
};

export function getStatusBadgeVariant(status: string): 'default' | 'success' | 'warning' | 'danger' | 'info' | 'earth' {
  const found = WORK_ORDER_STATUSES.find(s => status.startsWith(s.value.split('.')[0]));
  return found?.variant || 'default';
}

export function getStatusLabel(status: string): string {
  const found = WORK_ORDER_STATUSES.find(s => status.startsWith(s.value.split('.')[0]));
  return found?.label || status;
}

export function getNextStatuses(currentStatus: string): typeof WORK_ORDER_STATUSES {
  const validNextStatuses = STATUS_TRANSITIONS[currentStatus] || [];
  return WORK_ORDER_STATUSES.filter(s => validNextStatuses.includes(s.value));
}

export function isTerminalStatus(status: string): boolean {
  return status.startsWith('5.') || status.startsWith('7.');
}

export function getStageForStatus(status: string): number {
  const found = WORK_ORDER_STATUSES.find(s => s.value === status);
  return found?.stage ?? 0;
}

/**
 * What to show at click time for a status move: the new status (and location) in the detail and in every cached
 * work-order list. Complete and Declined are NOT shown early: the server stamps the completion time and cost fields
 * for them, and the owner has not yet ruled they are safe to undo (docs/plans/2026-10-21-fast-save-baseline.md 6.2).
 * An empty list means the screen waits for the server (spinner).
 */
function statusPatches(workorderId: number, newStatus: string, location?: { id: string | null; name?: string }): RowPatch[] {
  if (newStatus.startsWith('5.') || newStatus.startsWith('7.')) return [];
  const changes: Record<string, unknown> = {
    status: newStatus,
    ...(location && { currentLocationId: location.id, currentLocationName: location.name ?? null }),
  };
  return [
    { queryKey: ['workorder', workorderId], rowId: workorderId, changes },
    { queryKey: ['workorders'], rowId: workorderId, changes, appliesTo: isWorkOrderListCache },
  ];
}

/**
 * Caches under the ['workorders'] prefix that are lists of work orders: the filtered lists (['workorders', {filters}]),
 * the pipeline stage, ready-for-pickup and deck lists. Everything else under the prefix ('runs', 'stats', 'by-client',
 * ...) holds other entities whose rows can carry the same numeric id, so it must never be patched. The row must also
 * look like a work order (a string `status`). Infinite caches (urgent view) are not `{ data }` shaped and are skipped
 * by the helper; they show the old status until the refetch after the save.
 */
const WORK_ORDER_LIST_SEGMENTS = new Set(['stage', 'ready-for-pickup', 'deck']);
function isWorkOrderListCache(cacheKey: readonly unknown[], row: Record<string, unknown>): boolean {
  const segment = cacheKey[1];
  const listShaped =
    segment === undefined || (typeof segment === 'object' && segment !== null) || (typeof segment === 'string' && WORK_ORDER_LIST_SEGMENTS.has(segment));
  return listShaped && typeof row.status === 'string';
}

const ALREADY_SAVING = 'This work order is already saving a change. Wait for it to finish, then try again.';

/** Refetched after every status save settles: the server's answer is what stays on screen. */
const statusInvalidations = (workorderId: number) => [['workorders'], ['workorder', workorderId], ['workorders-stats']];

// =============================================================================
// STATUS CHANGE MODAL
// =============================================================================

interface StatusChangeModalProps {
  workorder: WorkOrder;
  onClose: () => void;
  onStatusChanged: () => void;
}

export function StatusChangeModal({
  workorder,
  onClose,
  onStatusChanged,
}: StatusChangeModalProps) {
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(
    workorder.currentLocationId || null
  );
  const [selectedLocationName, setSelectedLocationName] = useState<string | undefined>(
    workorder.currentLocationName || undefined
  );

  // The options are fixed at the status the modal opened with: the optimistic change moves `workorder.status` while
  // the save runs, which would otherwise swap the option list under the person (the selected option could vanish).
  const [openedStatus] = useState(workorder.status);
  const nextStatuses = useMemo(() => getNextStatuses(openedStatus), [openedStatus]);

  // Check if selected status is a "Ready" status (stage 4)
  const isReadyStatus = selectedStatus?.startsWith('4.');

  // Reset location when status changes away from Ready
  useEffect(() => {
    if (!isReadyStatus) {
      setSelectedLocationId(workorder.currentLocationId || null);
      setSelectedLocationName(workorder.currentLocationName || undefined);
    }
  }, [isReadyStatus, workorder.currentLocationId, workorder.currentLocationName]);

  const updateMutation = useOptimisticMutation<{ newStatus: string; locationId?: string | null; locationName?: string }>({
    mutationKey: ['workorder-status'],
    rowKey: () => workorder.id,
    request: ({ newStatus, locationId, locationName }) =>
      // Use updateStatus for status-only changes, or full update if location changes
      locationId !== undefined
        ? api.workorders.update(workorder.id, {
            status: newStatus,
            currentLocationId: locationId,
            currentLocationName: locationName || null,
          })
        : api.workorders.updateStatus(workorder.id, newStatus),
    patch: ({ newStatus, locationId, locationName }) =>
      statusPatches(workorder.id, newStatus, locationId !== undefined ? { id: locationId, name: locationName } : undefined),
    invalidate: statusInvalidations(workorder.id),
    // No error toast: this modal shows the reason once, in the inline error box below (the modal stays open on a refusal).
    // A click dropped because another component is saving this row is told to the person here too.
    onBlocked: () => toast.warning(ALREADY_SAVING),
    onSuccess: () => {
      onStatusChanged();
      onClose();
    },
  });

  const rowSaving = updateMutation.isRowSaving(workorder.id);

  const handleConfirm = () => {
    if (selectedStatus) {
      // Include location info for Ready statuses
      if (isReadyStatus) {
        updateMutation.mutate({
          newStatus: selectedStatus,
          locationId: selectedLocationId,
          locationName: selectedLocationName,
        });
      } else {
        updateMutation.mutate({ newStatus: selectedStatus });
      }
    }
  };

  const handleLocationChange = (locationId: string | null, locationName?: string) => {
    setSelectedLocationId(locationId);
    setSelectedLocationName(locationName);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center animate-fade-in p-4">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-xl animate-slide-up">
        {/* Header */}
        <div className="px-6 py-4 border-b border-sage-200">
          <h3 className="font-display text-lg font-semibold text-forest-900">
            Update Status
          </h3>
          <p className="text-sm text-sage-600 mt-1">
            Current: <Badge variant={getStatusBadgeVariant(workorder.status)} size="sm">
              {getStatusLabel(workorder.status)}
            </Badge>
          </p>
        </div>

        {/* Status Options */}
        <div className="p-6 space-y-2">
          {nextStatuses.length === 0 ? (
            <p className="text-center text-sage-500 py-4">
              This work order cannot be moved to another status.
            </p>
          ) : (
            nextStatuses.map((status) => (
              <button
                key={status.value}
                onClick={() => setSelectedStatus(status.value)}
                className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                  selectedStatus === status.value
                    ? 'border-forest-500 bg-forest-50'
                    : 'border-sage-200 hover:border-sage-300 bg-white'
                }`}
              >
                <Badge variant={status.variant} size="sm">
                  {status.label}
                </Badge>
                <span className="text-sm text-sage-600 flex-1 text-left">
                  {status.value.startsWith('0.') && 'Return to new / reopen'}
                  {status.value.startsWith('1.') && 'Move to processing'}
                  {status.value.startsWith('2.') && 'Materials ordered from supplier'}
                  {status.value.startsWith('3.') && 'Materials received at branch'}
                  {status.value.startsWith('4.') && 'Ready for delivery / pickup'}
                  {status.value.startsWith('5.') && 'Mark as finished'}
                  {status.value.startsWith('6.') && 'Flag for decline review'}
                  {status.value.startsWith('7.') && 'Confirm decline'}
                </span>
                {selectedStatus === status.value && (
                  <svg className="w-5 h-5 text-forest-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            ))
          )}
        </div>

        {/* Location Selector for Ready statuses */}
        {isReadyStatus && (
          <div className="px-6 pb-4">
            <div className="bg-sage-50 rounded-xl p-4">
              <LocationSelector
                value={selectedLocationId}
                onChange={handleLocationChange}
                branchId={workorder.branchId || undefined}
                includePrimary={true}
                primaryLabel="Primary Branch Location"
                label="Physical Location"
                placeholder="Where is this item located?"
              />
              <p className="text-xs text-sage-500 mt-2">
                {selectedStatus === '4. Ready for Pickup' && 'Default: Primary branch location (tech will pick up)'}
                {selectedStatus === '4. Ready for Run' && 'Default: Primary branch location (ops will put on run)'}
                {selectedStatus === '4. For South Bay' && 'Select the alternate location where item was sent'}
              </p>
            </div>
          </div>
        )}

        {/* Another component (a quick button) is saving this same work order */}
        {rowSaving && !updateMutation.isPending && (
          <div className="px-6 pb-4">
            <div role="status" className="bg-sage-50 border border-sage-200 rounded-xl p-3">
              <p className="text-sm text-sage-700">{ALREADY_SAVING}</p>
            </div>
          </div>
        )}

        {/* Error Display */}
        {updateMutation.error && (
          <div className="px-6 pb-4">
            <div className="bg-earth-50 border border-earth-200 rounded-xl p-3">
              <p className="text-sm text-earth-700">
                {updateMutation.error instanceof Error
                  ? updateMutation.error.message
                  : 'Failed to update status'}
              </p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="px-6 py-4 border-t border-sage-200 flex gap-3">
          <Button
            variant="ghost"
            className="flex-1"
            onClick={onClose}
            disabled={updateMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="flex-1"
            onClick={handleConfirm}
            disabled={!selectedStatus || updateMutation.isPending || rowSaving}
            isLoading={updateMutation.isPending}
          >
            Update Status
          </Button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// QUICK STATUS BUTTONS (for inline use)
// =============================================================================

interface QuickStatusButtonsProps {
  workorder: WorkOrder;
  onStatusChanged?: () => void;
  compact?: boolean;
}

export function QuickStatusButtons({
  workorder,
  onStatusChanged,
  compact = false,
}: QuickStatusButtonsProps) {

  const updateMutation = useOptimisticMutation<string>({
    mutationKey: ['workorder-status'],
    rowKey: () => workorder.id,
    request: (newStatus) => api.workorders.updateStatus(workorder.id, newStatus),
    patch: (newStatus) => statusPatches(workorder.id, newStatus),
    invalidate: statusInvalidations(workorder.id),
    errorToast: 'Could not update the status',
    // A click dropped because this row is already saving (here or in the modal) is told to the person.
    onBlocked: () => toast.warning(ALREADY_SAVING),
    onSuccess: () => onStatusChanged?.(),
  });
  const rowSaving = updateMutation.isRowSaving(workorder.id);

  const nextStatuses = getNextStatuses(workorder.status);

  if (nextStatuses.length === 0) return null;

  // For compact mode, only show the most logical next step
  const primaryNextStatus = nextStatuses[0];
  const completeStatus = nextStatuses.find(s => s.value.startsWith('5.'));

  if (compact) {
    return (
      <div className="flex gap-2">
        {primaryNextStatus && !primaryNextStatus.value.startsWith('7.') && (
          <Button
            variant="secondary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              updateMutation.mutate(primaryNextStatus.value);
            }}
            isLoading={updateMutation.isPending}
            disabled={rowSaving}
          >
            {primaryNextStatus.label}
          </Button>
        )}
        {completeStatus && (
          <Button
            variant="primary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              updateMutation.mutate(completeStatus.value);
            }}
            isLoading={updateMutation.isPending}
            disabled={rowSaving}
          >
            Complete
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {nextStatuses.map((status) => (
        <Button
          key={status.value}
          variant={status.value.startsWith('5.') ? 'primary' : status.value.startsWith('7.') ? 'danger' : 'secondary'}
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            updateMutation.mutate(status.value);
          }}
          isLoading={updateMutation.isPending}
          disabled={rowSaving}
        >
          {status.label}
        </Button>
      ))}
    </div>
  );
}
